
import dayjs from 'dayjs'
import { mapState, mapGetters } from 'vuex'
import messages from '~/locales/checkin.translate'
import sharedMessages from '~/locales/common.translate'
import { ROLE_CAPTAIN } from '~/utils/constants'
import { getErrorMessage } from '~/utils/errors'
import { isMonthlyQualifier } from '~/utils/segmentPhase'
import { sleep } from '~/utils/sleep'

export default {
	i18n: {
		messages,
		sharedMessages,
	},
	computed: {
		...mapState(['scToken']),
		...mapState('event', ['activeSegment', 'activeEvent']),
		...mapGetters('event', ['rosterMinSize']),
		...mapState('user', [
			'middlewareRosterId',
			'clanMembers',
			'clan',
			'userId',
			'showSegmentCheckInModal',
			'latestRoster',
		]),

		isMonthlyQualifier() {
			return isMonthlyQualifier(this.activeSegment)
		},
	},
	data: () => ({
		loading: false,
		validSelectedRoster: false,
		players: [],
	}),
	mounted() {
		this.$nuxt.$on('checkbox-input-changed', () => {
			this.onCheckboxInputChanged()
		})
	},
	beforeDestroy() {
		this.$nuxt.$off('checkbox-input-changed')
	},
	watch: {
		showSegmentCheckInModal(val) {
			if (val) {
				this.loading = false
				this.players = this.clanMembers
					.filter((m) => m.isRosterMember && m.userId)
					.map((i) => {
						i.isSelected = this.isCaptain(i)
						return i
					})
				this.onCheckboxInputChanged()
			}
		},
	},
	methods: {
		isCaptain(player) {
			return (
				player.rosterPosition === ROLE_CAPTAIN &&
				player.townHallLevel === this.clan?.thLevel
			)
		},
		onCloseSegmentCheckInModal() {
			this.loading = false
			this.$store.commit('user/setShowSegmentCheckInModal', false)
		},
		// eslint-disable-next-line complexity
		async checkinForSegment() {
			try {
				this.loading = true

				const activeSegment = this.activeSegment

				if (!activeSegment || !this.clan || !this.middlewareRosterId || !this.latestRoster)
					throw new Error('Invalid data')

				// Check if the roster is checked in already
				if (this.latestRoster?.checkedIn) {
					throw new Error('Roster already checked in')
				}

				// Check if the user has a roster for the active segment
				if (this.latestRoster?.segment?.id !== activeSegment.id) {
					throw new Error('Latest roster is not for the active segment')
				}

				// Check if the user is the captain
				if (this.latestRoster?.owner?.id !== this.userId) {
					throw new Error('User is not the captain')
				}

				if (
					activeSegment.segmentCheckinsConfig &&
					activeSegment.segmentCheckinsConfig.enabled &&
					activeSegment.segmentCheckinsConfig.openWindow
				) {
					const now = dayjs()
					const from = dayjs(activeSegment.segmentCheckinsConfig.openWindow.from)
					const to = dayjs(activeSegment.segmentCheckinsConfig.openWindow.to)

					if (!now.isAfter(from) || !now.isBefore(to)) {
						throw new Error('Not in checkin window')
					}
				}

				if (this.isMonthlyQualifier) {
					// in case of monthly qualifier, the captain checkin all players
					await this.$api.rosters.checkins.checkinRoster(this.latestRoster.id)
				} else {
					// in case of monthly finals, the captain it's allwed to select 5 out 6 players
					const selectedPlayers = this.players
						.filter((p) => p.isSelected)
						.map((p) => p.tag)

					if (selectedPlayers.length !== this.rosterMinSize) {
						throw new Error('Invalid roster size')
					}

					await this.$warApi.setSelectedPlayers(
						this.scToken,
						this.clan.tag,
						selectedPlayers
					)

					await this.$scApi.checkSelectedPlayers(this.scToken)

					await this.$scApi.segmentCheckInRoster(
						this.scToken,
						this.activeSegment.id,
						selectedPlayers
					)
				}

				// Wait for the roster to be checked in
				while (!this.latestRoster?.checkedIn) {
					await sleep(2000)
					await this.$store.dispatch('user/getRoster', {
						eventId: this.activeEvent.id,
						userId: this.userId,
					})
				}
			} catch (error) {
				this.$logApi.createLogMessage(
					`checkinForSegment error: ${JSON.stringify(
						error
					)}, this.players: ${JSON.stringify(this.players)}`
				)

				const errorPath = getErrorMessage(error)
				this.$router.push(this.localePath(errorPath))
			} finally {
				this.loading = false
				this.$store.commit('user/setShowSegmentCheckInModal', false)
			}
		},
		onCheckboxInputChanged() {
			if (this.isMonthlyQualifier) {
				this.validSelectedRoster = true
				return
			}

			const amountPlayers = this.players.filter((p) => p.isSelected).length
			this.validSelectedRoster = amountPlayers === this.rosterMinSize
		},
	},
}
